import React, { useState, useEffect, useMemo } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Container,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
  IconButton,
  Box,
  Stack,
  Paper,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import Triggers from './Triggers';
import { useNotification } from './context/NotificationContext';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0c0d12',
      paper: '#14171f',
    },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: {
      main: '#6aa9ff',
    },
    success: {
      main: '#5ad27a',
    },
    error: {
      main: '#ff6b6b',
    },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.02)',
    color: '#e8e9ee',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: '#6aa9ff' },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(232,233,238,0.7)',
  },
};

function BatchScheduler() {
  const { showNotification } = useNotification();
  const [value, setValue] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [pendingPosts, setPendingPosts] = useState([]);

  useEffect(() => {
    loadPendingPosts();
  }, []);

  useEffect(() => {
    document.title = 'Scheduler';
  }, []);

  const loadPendingPosts = () => {
    fetch('https://frek.cc/proxy/38.54.86.24:9911/pending_posts')
      .then((response) => response.json())
      .then((data) => {
        setPendingPosts(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error('Error fetching pending posts:', error);
        handleNotification('Failed to fetch pending schedule.');
      });
  };

  const cancelPost = (taskId) => {
    if (window.confirm('Are you sure you want to cancel this batch opening?')) {
      fetch('https://frek.cc/proxy/38.54.86.24:9911/cancel_post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: taskId }),
      })
        .then((response) => response.json())
        .then((data) => {
          handleNotification(data.message);
          loadPendingPosts();
        })
        .catch((error) => {
          console.error('Error:', error);
          handleNotification('Failed to cancel post.');
        });
    }
  };

  const submitPost = () => {
    const dateTime = `${date} ${time}:00`;

    const formattedDate = new Date(`${date}T${time}`).toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });

    const splitValues = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    const multipliedValue = splitValues.length * 32;

    const initialMessage = `${multipliedValue} numbers will be added for registration on ${formattedDate}. Set your alarm now guys!`;
    const upMessage = `${multipliedValue} numbers is now added for registration! You can now start to register.`;

    const data = {
      initial_message: initialMessage,
      value,
      time: dateTime,
      up_message: upMessage,
    };

    fetch('https://frek.cc/proxy/38.54.86.24:9911/schedule_post', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then(() => {
        handleNotification('Post scheduled successfully!');
        loadPendingPosts();
      })
      .catch((error) => {
        console.error('Error:', error);
        handleNotification('Failed to schedule post.');
      });
  };

  const handleNotification = (message) => {
    showNotification(message, 'info');
  };

  const numbersCount = useMemo(() => {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean).length;
  }, [value]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack spacing={1.2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Scheduler
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                      Schedule batch openings and manage pending runs.
                    </Typography>
                  </Box>

                  <Tooltip title="Refresh pending schedule">
                    <Button startIcon={<RefreshIcon />} variant="contained" size="small" onClick={loadPendingPosts}>
                      Refresh
                    </Button>
                  </Tooltip>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={`Pending ${pendingPosts.length}`}
                    sx={{ bgcolor: 'rgba(255,107,107,0.18)', color: '#ffb3b3', fontWeight: 700 }}
                  />
                  <Chip
                    label={`Numbers ${numbersCount}`}
                    sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Batch"
                    variant="outlined"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="ex: 0917..., 0928..., ..."
                    fullWidth
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={3.5}>
                  <TextField
                    label="Date"
                    type="date"
                    variant="outlined"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={3.5}>
                  <TextField
                    label="Time"
                    type="time"
                    variant="outlined"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="success"
                onClick={submitPost}
                sx={{ mt: 1.5 }}
                disabled={!value || !date || !time}
              >
                Schedule
              </Button>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2, py: 1.3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Pending Scheduled</Typography>
              </Box>

              <List sx={{ py: 0 }}>
                {pendingPosts.length === 0 ? (
                  <Typography sx={{ px: 2, py: 3, color: 'rgba(255,255,255,0.55)' }}>No Scheduled.</Typography>
                ) : (
                  pendingPosts.map((post) => (
                    <ListItem
                      key={post.task_id}
                      secondaryAction={
                        <IconButton edge="end" aria-label="delete" onClick={() => cancelPost(post.task_id)}>
                          <DeleteIcon sx={{ color: '#ff8a8a' }} />
                        </IconButton>
                      }
                      sx={{
                        borderBottom: '1px solid rgba(255,255,255,0.07)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{ sx: { color: '#e8e9ee' } }}
                        secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.68)' } }}
                        primary={`Batch: ${post.value}`}
                        secondary={`Time: ${post.time} | Follow-up: ${post.up_message}`}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>

            <Triggers />
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default BatchScheduler;
